import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import Link from '@atlaskit/link';
import { AtlassianIcon } from '@atlaskit/logo';
import { Mention } from '@atlaskit/mention';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { ProfileCardTrigger, type ProfileClient } from '@atlaskit/profilecard';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type Agent,
	type AssistanceServiceProduct,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import { isCustomerAgent, isForgeAgent, isOOTBAgent, isSystemAgent } from '../../common/types.ts';
import { useAgentCrossProductUrlWrapper } from '../../common/utils/use-agent-cross-product-url/use-agent-cross-product-url.tsx';
import type { AgentDetailsWithAggData } from '../../controllers/agent-details/index.ts';
import {
	DEFAULT_AGENTS_NAMED_ID_LIST,
	DEFAULT_OOTB_AGENTS_NAMED_IDS,
} from '../../controllers/agents/constants.ts';
import { useAtlasURL } from '../../controllers/atlas-url/index.ts';
import { useProfileClient } from '../../controllers/profile-client/index.ts';

import messages from './messages.ts';

type AgentCreatedByProps = {
	agent: Agent;
	siteId: string;
	simplified?: boolean;
	onClick?: React.MouseEventHandler<HTMLElement>;
};

export const getAAIDFromARI = (ari: string): string | undefined => {
	const matched = ari.match(/\/([a-zA-Z0-9_\|\-\:]{1,128})$/);
	return matched ? matched[1] : undefined;
};

export const getARIFromAAID = (aaid: string): string => {
	return `ari:cloud:identity::user/${aaid}`;
};

/**
 * Renders an at-mention for the user who created this agent
 * Usage: Browse agent modal
 * @private
 * @deprecated use platform/packages/ai-mate/conversation-assistant-agent/src/common/ui/creator/index.tsx instead
 * @param agent - Agent
 * @returns JSX
 */
export const AgentCreatedBy = ({
	agent,
	siteId,
	simplified,
	onClick,
}: AgentCreatedByProps): React.JSX.Element | null => {
	if (isSystemAgent(agent) || isOOTBAgent(agent) || !agent.creator) {
		return (
			<Inline alignBlock="center" testId="atlassian-icon">
				<AtlassianIcon label="" appearance="brand" size="small" />
				<Box>Atlassian</Box>
			</Inline>
		);
	}

	if (isCustomerAgent(agent)) {
		const authoringTeam = agent.authoring_team;

		if (authoringTeam?.id) {
			return (
				<Inline alignBlock="center" testId="atlassian-icon">
					<Link href={authoringTeam.profile_url} target="_blank" onClick={onClick}>
						{authoringTeam.display_name}
					</Link>
				</Inline>
			);
		}

		const aaid = agent.creator ? getAAIDFromARI(agent.creator) : undefined;
		if (!aaid) {
			return null;
		}

		return (
			<ResolvingMention
				userId={aaid}
				siteId={siteId}
				profileCard
				simplified={simplified}
				onClick={onClick}
			/>
		);
	}

	if (
		fg('rovo_agent_support_a2a_avatar')
			? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
			: isForgeAgent(agent)
	) {
		if (
			agent.creator_type === 'REMOTE_A2A' &&
			FeatureGates.getExperimentValue('rovo_hide_remote_a2a_agent_creator_exp', 'isEnabled', false)
		) {
			return null;
		}
		return <Box>{agent.creator}</Box>;
	}

	return null;
};

type ResolvingMentionProps = {
	userId: string;
	siteId: string;
	profileCard?: boolean;
	simplified?: boolean;
	renderWhenHidden?: boolean;
	onClick?: React.MouseEventHandler<HTMLElement>;
};

// TODO consolidate this with `AgentCreator` component `platform/packages/ai-mate/conversation-assistant-agent/src/common/ui/creator/index.tsx`
const ResolvingMention = ({
	userId,
	siteId,
	profileCard = false,
	simplified = false,
	onClick,
}: ResolvingMentionProps) => {
	di(useProfileResolution, useAgentCrossProductUrlWrapper);
	const { profile, client } = useProfileResolution(userId, siteId);
	const { formatMessage } = useIntl();
	const { atlasURL } = useAtlasURL(siteId);
	const wrapCrossProductUrl = useAgentCrossProductUrlWrapper({
		xpcBridge: 'rovoAgents-browseAgentsModal',
	});

	const fullName =
		profile.data?.status === 'inactive' && profile.data.fullName
			? formatMessage(messages.deactivatedCreatorName, { name: profile.data.fullName })
			: profile.data?.fullName;

	if (profile.isFetching || !profile.data) {
		return <Skeleton isShimmering height={18} width={100} borderRadius={token('radius.small')} />;
	}

	if (simplified) {
		const profileUrl = wrapCrossProductUrl(atlasURL(`/people/${userId}`));

		return (
			<Link href={profileUrl} target="_blank" onClick={onClick}>
				{fullName}
			</Link>
		);
	}

	return profileCard ? (
		<ProfileCardTrigger userId={userId} resourceClient={client} ariaLabel={fullName}>
			<Mention id={userId} isHighlighted={profile.data.isCurrentUser} text={`@${fullName}`} />
		</ProfileCardTrigger>
	) : (
		<Mention id={userId} isHighlighted={profile.data.isCurrentUser} text={`@${fullName}`} />
	);
};

export type ProfileState = {
	avatarUrl?: string;
	fullName?: string;
	nickname?: string;
	email?: string;
	location?: string;
	isCurrentUser?: boolean;
	timestring?: string;
	status?: 'active' | 'inactive';
	companyName?: string;
	isBot?: boolean;
	meta?: string;
	statusModifiedDate?: string;
};

export function useProfileResolution(
	userId: string,
	siteId: string,
	shouldFetch: boolean = true,
): {
	profile: {
		data: ProfileState | null;
		error: ApiError | null;
		isFetching: boolean;
		refetch: () => Promise<ProfileState | null>;
	};
	client: ProfileClient;
} {
	const profileClient = useProfileClient();

	const profile = useQuery({
		queryKey: ['fetchProfile', siteId, userId],
		queryFn: async (): Promise<ProfileState> => {
			try {
				const response = await profileClient.getProfile(siteId, userId);
				return response;
			} catch (error) {
				logException({
					exception: error,
					name: 'ConversationAssistantAgentUseProfileResolutionError',
				});
				return {};
			}
		},
		enabled: shouldFetch,
	});

	return { profile, client: profileClient };
}

export const filterAndSortAgents = (
	agents: Agent[],
	filters: {
		searchValue: string;
	},
): Agent[] => {
	return agents
		.filter(
			(agent) =>
				agent.name.toLocaleLowerCase().includes(filters.searchValue.toLocaleLowerCase()) ||
				agent.description?.toLocaleLowerCase().includes(filters.searchValue.toLocaleLowerCase()),
		)
		.sort((a, b) => a.name.localeCompare(b.name));
};

/** @deprecated Use getEnabledKnowledgeFiltersFromAgent. Remove after rovo_agents_unit_scoped cleanup. */
export const getEnabledKnowlegeArisFromAgent = (
	agent: Pick<AgentDetailsWithAggData, 'scenarios' | 'knowledge_sources'>,
): string[] => {
	const enabledSources = agent.scenarios
		?.filter((scenario) => scenario.isActive && scenario.isValid?.value)
		?.flatMap((scenario) => scenario.knowledgeSources)
		.filter((source) => source?.enabled)
		.flatMap((source) => source?.sources)
		.filter((source) => source?.enabled);

	if (!enabledSources?.length) {
		return [];
	}

	const allAris = enabledSources.reduce((acc, cur) => {
		const allFilters = [
			...(cur?.filters?.parentFilter ?? []),
			...(cur?.filters?.spaceFilter ?? []),
			...(cur?.filters?.projectFilter ?? []),
		];

		allFilters.forEach((item) => acc.add(item));

		return acc;
	}, new Set<string>());

	return Array.from(allAris);
};

export type AgentKnowledgeFilter = {
	ari: string;
	cloudId?: string | null;
};

export const getEnabledKnowledgeFiltersFromAgent = (
	agent: Pick<AgentDetailsWithAggData, 'scenarios' | 'knowledge_sources'>,
): AgentKnowledgeFilter[] => {
	const enabledSources = agent.scenarios
		?.filter((scenario) => scenario.isActive && scenario.isValid?.value)
		?.flatMap((scenario) => scenario.knowledgeSources)
		.filter((source) => source?.enabled)
		.flatMap((source) => source?.sources)
		.filter((source): source is NonNullable<typeof source> => source?.enabled === true);
	const filtersByAri = new Map<string, AgentKnowledgeFilter>();

	enabledSources?.forEach((source) => {
		const sourceAris = [
			...(source.filters?.parentFilter ?? []),
			...(source.filters?.spaceFilter ?? []),
			...(source.filters?.projectFilter ?? []),
		];

		sourceAris.forEach((ari) => {
			if (!filtersByAri.has(ari)) {
				filtersByAri.set(ari, { ari, cloudId: source.cloudId ?? null });
			}
		});
	});

	return [...filtersByAri.values()];
};

export const getSuggestedOotbAgents = ({
	availableAgents,
	product,
}: {
	availableAgents: Agent[];
	product?: AssistanceServiceProduct;
}): Agent[] => {
	// If no product is provided / product is not supported, fallback to using default agents
	const defaultAgentsNamedIds = product
		? (DEFAULT_OOTB_AGENTS_NAMED_IDS[product] ?? DEFAULT_AGENTS_NAMED_ID_LIST)
		: DEFAULT_AGENTS_NAMED_ID_LIST;

	const defaultAgents = availableAgents.filter(
		(agent) =>
			defaultAgentsNamedIds.has(agent.named_id) ||
			(agent.external_config_reference &&
				defaultAgentsNamedIds.has(agent.external_config_reference)),
	);

	return defaultAgents;
};
